import { useSelector } from 'react-redux';
import {
  selectIsMIApplyButtonFeatureFlagOn,
  selectIsDynamicContentReportingSnippetsFeatureFlagOn,
} from 'core/userContext';
import { useMetricsContext } from 'modules/productAnalytics/context/MetricsContext';
import { useCampaignAndMetricFiltersContext } from 'modules/messagingInsights/context/CampaignAndMetricFiltersContext';
import { useTabSpecificFiltersContext } from 'modules/messagingInsights/context/TabSpecificFiltersContext';
import { useTimeFiltersContext } from 'modules/messagingInsights/context/TimeFiltersContext';
import {
  selectAppliedCrossMetrics,
  selectAppliedMediums,
} from 'modules/messagingInsights/selectors';
import { getHighLevelAndMedMetricsFromMetrics } from 'modules/messagingInsights/utils';
import { SavedViewSettings } from 'modules/messagingInsights/types';

/**
 * Compiles an object containing all view settings that are currently reflected/applied in the UI.
 */
export const useGetCurrSettings = () => {
  const MIApplyButtonFF = useSelector(selectIsMIApplyButtonFeatureFlagOn);
  const appliedMediums = useSelector(selectAppliedMediums);
  const appliedCrossMetrics = useSelector(selectAppliedCrossMetrics);
  const isDCRFFOn = useSelector(selectIsDynamicContentReportingSnippetsFeatureFlagOn);

  const {
    campaignsSortedBy,
    campaignsSortDirection,
    audienceSortedBy,
    audienceSortDirection,
    selectedSegment,
    region,
    snippetsSortedBy,
    snippetsSortDirection,
  } = useTabSpecificFiltersContext();
  const { dateParams, timePreset, timezone } = useTimeFiltersContext();
  const {
    appliedCampaignFilters,
    campaignFilters,
    selectedMediums,
    overallMetrics,
    mediumMetrics,
  } = useCampaignAndMetricFiltersContext();
  const { highLevelMetrics, analyticsMetrics } = useMetricsContext();

  const { mediumMetrics: appliedMediumMetrics, overallMetrics: appliedOverallMetrics } =
    getHighLevelAndMedMetricsFromMetrics(appliedCrossMetrics, highLevelMetrics, analyticsMetrics);
  const settings: SavedViewSettings = {
    tabOpts: {
      campaignsSortedBy,
      campaignsSortDirection,
      audienceSortedBy,
      audienceSortDirection,
      selectedSegment,
      region,
    },
    timeFilters: {
      startTime: dateParams?.startTime?.toISOString(),
      endTime: dateParams?.endTime?.toISOString(),
      timePreset,
      timezone,
    },
    campaignFilters: MIApplyButtonFF ? appliedCampaignFilters : campaignFilters,
    selectedMetrics: {
      overallMetrics: MIApplyButtonFF ? appliedOverallMetrics : overallMetrics,
      mediumMetrics: MIApplyButtonFF ? appliedMediumMetrics : mediumMetrics,
    },
    selectedMediums: MIApplyButtonFF ? appliedMediums : selectedMediums,
  };
  if (isDCRFFOn && snippetsSortedBy) {
    settings.tabOpts.snippetsSortedBy = snippetsSortedBy;
  }
  if (isDCRFFOn && snippetsSortDirection) {
    settings.tabOpts.snippetsSortDirection = snippetsSortDirection;
  }

  return settings;
};
